import React, { useEffect, useState, useCallback } from 'react';
import { apiGet } from '../lib/api';
import { inrCompact } from '../lib/format';
import {
  ShieldAlert, Clock, FileWarning, Wallet, RefreshCw, ArrowUpRight,
  Users, AlertOctagon, Activity, HardHat, FlaskConical, Sparkles, GitBranch,
  FileSignature, CalendarClock, Camera,
} from 'lucide-react';

const ExecKpi = ({ overline, value, sub, icon: Icon, accent, testid, link }) => {
  const body = (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-300">{overline}</div>
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${accent}`}>
          <Icon size={15} />
        </div>
      </div>
      <div className="font-display text-3xl font-extrabold tracking-tight text-white tabular-nums">{value}</div>
      {sub && <div className="mt-1.5 text-xs text-slate-400 leading-snug">{sub}</div>}
    </div>
  );
  const cls = "bg-[#1e2540] border border-white/10 rounded-sm p-5 hover:border-[#5b6aa8]/60 transition-colors";
  return link
    ? <a href={link} className={cls} data-testid={testid}>{body}</a>
    : <div className={cls} data-testid={testid}>{body}</div>;
};

const SignalChip = ({ children }) => (
  <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 mr-1 mb-1 rounded-sm bg-red-50 text-red-700 border border-red-200">
    {children}
  </span>
);

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [aiSignals, setAiSignals] = useState([]);

  const load = useCallback((force = false) => {
    if (force) setRefreshing(true);
    apiGet(`/dashboard/exec${force ? '?refresh=true' : ''}`)
      .then((d) => { setData(d); setForbidden(false); })
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
      })
      .finally(() => { setLoading(false); setRefreshing(false); });
    // Stored High/Critical delay predictions only — never triggers a live Claude call.
    apiGet('/ai/risk-signals')
      .then((d) => setAiSignals(d.signals || []))
      .catch(() => { /* AI feature optional — fail silently */ });
  }, []);

  useEffect(() => { load(false); }, [load]);

  // Non-exec users: render nothing (no empty placeholders, no broken UI).
  if (forbidden) return null;
  if (loading) {
    return <div className="mb-6 text-sm text-slate-500 font-mono" data-testid="command-center-loading">Loading command center…</div>;
  }
  if (!data) return null;

  const ap = data.approvals_pending || {};
  const fraud = data.fraud_summary || {};
  const tenders = data.tenders_imminent || {};
  const cash = data.cash_proxy || {};
  const labour = data.labour_today || {};
  const delay = data.delay_signals || {};
  const risk = data.risk_projects || [];
  const oldest = ap.oldest_items || [];

  const totalApprovals = (ap.po_pending_count || 0) + (ap.mpr_pending_pm_count || 0)
    + (ap.leave_pending_count || 0) + (ap.payroll_pending_count || 0);
  const mtdOutflow = (cash.petty_cash_mtd || 0) + (cash.fuel_mtd || 0);
  const genTime = data.generated_at
    ? new Date(data.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
    : '';

  return (
    <div className="mb-7" data-testid="command-center">
      {/* Header + manual refresh */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">CEO Command Center</div>
          <div className="text-xs text-slate-500">Current state across the business · not affected by the period filter below</div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
          data-testid="command-center-refresh"
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing' : 'Refresh'}
          {genTime && <span className="text-slate-400 normal-case font-normal tracking-normal ml-1">· {genTime}</span>}
        </button>
      </div>

      {/* Top KPI strip — dark band to set it apart from period-filtered widgets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ExecKpi
          overline="Approvals Pending"
          value={totalApprovals}
          sub={`${ap.po_pending_count || 0} PO · ${ap.mpr_pending_pm_count || 0} MPR · ${ap.leave_pending_count || 0} leave · ${ap.payroll_pending_count || 0} payroll`}
          icon={Clock}
          accent="bg-amber-500/15 text-amber-300"
          testid="cc-kpi-approvals"
        />
        <ExecKpi
          overline="Fraud Alerts"
          value={fraud.open || 0}
          sub={`${fraud.new_today || 0} new today · open & unresolved`}
          icon={ShieldAlert}
          accent="bg-red-500/15 text-red-300"
          testid="cc-kpi-fraud"
          link="/fraud-alerts"
        />
        <ExecKpi
          overline="Tenders Due"
          value={(tenders.due_today || 0) + (tenders.due_tomorrow || 0)}
          sub={`${tenders.due_today || 0} today · ${tenders.due_tomorrow || 0} tomorrow`}
          icon={FileWarning}
          accent="bg-blue-500/15 text-blue-300"
          testid="cc-kpi-tenders"
        />
        <ExecKpi
          overline="MTD Outflow"
          value={inrCompact(mtdOutflow)}
          sub="Operational proxy · Finance module pending"
          icon={Wallet}
          accent="bg-emerald-500/15 text-emerald-300"
          testid="cc-kpi-cash"
        />
      </div>

      {/* Safety / HSE — Phase 4 KPI block */}
      {data.safety && (() => {
        const s = data.safety;
        const metrics = [
          { label: 'Open Critical Obs.', value: s.open_critical_observations || 0, danger: (s.open_critical_observations || 0) > 0 },
          { label: 'Open Critical Near-Miss', value: s.open_critical_near_miss || 0, danger: (s.open_critical_near_miss || 0) > 0 },
          { label: 'Overdue Actions', value: s.overdue_corrective_actions || 0, danger: (s.overdue_corrective_actions || 0) > 0 },
          { label: 'PPE Compliance 7d', value: s.ppe_compliance_7d != null ? `${s.ppe_compliance_7d}%` : '—', danger: s.ppe_compliance_7d != null && s.ppe_compliance_7d < 80 },
          { label: 'Toolbox Talks (wk)', value: s.toolbox_talks_this_week || 0, danger: false },
        ];
        return (
          <a href="/app/safety" className="block bg-[#1e2540] border border-white/10 rounded-sm p-5 mt-4 hover:border-[#5b6aa8]/60 transition-colors" data-testid="cc-safety-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-300 flex items-center gap-2">
                <span className="w-8 h-8 rounded-sm bg-red-500/15 text-red-300 flex items-center justify-center"><HardHat size={15} /></span>
                Safety / HSE
              </div>
              <span className="text-[11px] font-display font-bold uppercase tracking-widest text-[#9CA9D6] flex items-center gap-1">Open Safety <ArrowUpRight size={11} /></span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {metrics.map((m, i) => (
                <div key={i}>
                  <div className={`font-display text-2xl font-extrabold tabular-nums ${m.danger ? 'text-red-400' : 'text-white'}`}>{m.value}</div>
                  <div className="text-[10px] text-slate-400 leading-snug mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>
          </a>
        );
      })()}

      {/* Quality — Phase 4 slice 3 KPI block */}
      {data.quality && (() => {
        const q = data.quality;
        const metrics = [
          { label: 'Open High/Critical NCRs', value: q.open_critical_ncrs || 0, danger: (q.open_critical_ncrs || 0) > 0 },
          { label: 'Failed Cube Tests (30d)', value: q.failed_cube_tests_30d || 0, danger: (q.failed_cube_tests_30d || 0) > 0 },
          { label: 'Overdue CAPA Actions', value: q.overdue_capa_actions || 0, danger: (q.overdue_capa_actions || 0) > 0 },
        ];
        return (
          <a href="/app/quality/heatmap" className="block bg-[#1e2540] border border-white/10 rounded-sm p-5 mt-4 hover:border-[#5b6aa8]/60 transition-colors" data-testid="cc-quality-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-300 flex items-center gap-2">
                <span className="w-8 h-8 rounded-sm bg-cyan-500/15 text-cyan-300 flex items-center justify-center"><FlaskConical size={15} /></span>
                Quality
              </div>
              <span className="text-[11px] font-display font-bold uppercase tracking-widest text-[#9CA9D6] flex items-center gap-1">Open Heatmap <ArrowUpRight size={11} /></span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {metrics.map((m, i) => (
                <div key={i}>
                  <div className={`font-display text-2xl font-extrabold tabular-nums ${m.danger ? 'text-red-400' : 'text-white'}`}>{m.value}</div>
                  <div className="text-[10px] text-slate-400 leading-snug mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>
          </a>
        );
      })()}
      {/* Schedule Risk — Phase D (live CPM early-warning from daily DPR) */}
      {data.schedule_risk && (() => {
        const sr = data.schedule_risk;
        const metrics = [
          { label: 'Open Schedule Risks', value: sr.open_schedule_risks || 0, danger: (sr.open_schedule_risks || 0) > 0 },
          { label: 'On Critical Path', value: sr.open_critical_schedule_risks || 0, danger: (sr.open_critical_schedule_risks || 0) > 0 },
          { label: 'New (unseen)', value: sr.new_schedule_risks || 0, danger: (sr.new_schedule_risks || 0) > 0 },
        ];
        return (
          <a href="/app/projects" className="block bg-[#1e2540] border border-white/10 rounded-sm p-5 mt-4 hover:border-[#5b6aa8]/60 transition-colors" data-testid="cc-schedule-risk-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-300 flex items-center gap-2">
                <span className="w-8 h-8 rounded-sm bg-amber-500/15 text-amber-300 flex items-center justify-center"><GitBranch size={15} /></span>
                Schedule Risk · Live CPM
              </div>
              <span className="text-[11px] font-display font-bold uppercase tracking-widest text-[#9CA9D6] flex items-center gap-1">Open Projects <ArrowUpRight size={11} /></span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {metrics.map((m, i) => (
                <div key={i}>
                  <div className={`font-display text-2xl font-extrabold tabular-nums ${m.danger ? 'text-amber-300' : 'text-white'}`}>{m.value}</div>
                  <div className="text-[10px] text-slate-400 leading-snug mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>
          </a>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Approval Queue */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cc-approval-queue">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="kpi-overline">Approval Queue</div>
              <h3 className="font-display font-bold text-base mt-0.5">Oldest pending · top 10</h3>
            </div>
            <Clock size={15} className="text-amber-500" />
          </div>
          <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
            {oldest.length === 0 ? (
              <div className="p-5 text-sm text-slate-400">No pending approvals. All clear.</div>
            ) : (
              <table className="w-full dense-table">
                <thead><tr><th>Item</th><th>Module</th><th className="text-right">Value</th><th className="text-right">Age</th></tr></thead>
                <tbody>
                  {oldest.map((it, i) => (
                    <tr key={i} className="hover:bg-slate-50 cursor-pointer" onClick={() => it.link && (window.location.href = it.link)}>
                      <td className="text-[13px] text-[#0F172A] max-w-[220px] truncate">{it.item_label}</td>
                      <td className="text-xs text-slate-500">{it.module}</td>
                      <td className="text-right text-xs font-display font-bold">{it.value ? inrCompact(it.value) : '—'}</td>
                      <td className="text-right">
                        <span className={`text-xs font-display font-bold tabular-nums ${it.days_pending >= 7 ? 'text-red-600' : it.days_pending >= 3 ? 'text-amber-600' : 'text-slate-500'}`}>
                          {it.days_pending}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Top Risk Projects */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cc-risk-projects">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="kpi-overline">Risk Radar</div>
              <h3 className="font-display font-bold text-base mt-0.5">Top 10 projects at risk</h3>
            </div>
            <a href="/app/projects" className="text-[11px] font-display font-bold uppercase tracking-widest text-[#3b4872] hover:underline flex items-center gap-1">View <ArrowUpRight size={11} /></a>
          </div>
          <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
            {risk.length === 0 ? (
              <div className="p-5 text-sm text-slate-400">No risk signals across active projects.</div>
            ) : (
              <table className="w-full dense-table">
                <thead><tr><th>Project</th><th>Signals</th><th className="text-right">Score</th></tr></thead>
                <tbody>
                  {risk.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="text-[13px] font-display font-semibold text-[#0F172A] max-w-[160px] truncate">{r.project}</td>
                      <td className="py-2">{(r.signals || []).map((s, j) => <SignalChip key={j}>{s}</SignalChip>)}</td>
                      <td className="text-right">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-sm text-xs font-display font-extrabold ${r.score >= 6 ? 'bg-red-600 text-white' : r.score >= 3 ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                          {r.score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* AI Risk Signals — stored High/Critical delay predictions (read-only; no live Claude call) */}
      {(() => {
        const ai = data.ai_risk_signals || {};
        const cards = [
          { label: 'Contracts · Critical Risk', value: ai.contracts_with_critical_risk || 0, icon: FileSignature, href: '/contracts', danger: (ai.contracts_with_critical_risk || 0) > 0 },
          { label: 'High Absenteeism Risk (tmrw)', value: ai.high_absenteeism_risk_count || 0, icon: CalendarClock, href: '/labour-dashboard', danger: (ai.high_absenteeism_risk_count || 0) > 0 },
          { label: 'DPR Discrepancies (7d)', value: ai.dpr_discrepancies_7d || 0, icon: Camera, href: '/dpr', danger: (ai.dpr_discrepancies_7d || 0) > 0 },
        ];
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4" data-testid="cc-ai-signal-counters">
            {cards.map((c) => (
              <a key={c.label} href={c.href}
                className={`bg-white border rounded-sm p-4 flex items-center gap-3 hover:shadow-sm transition-shadow ${c.danger ? 'border-l-4 border-l-rose-500 border-slate-200' : 'border-slate-200'}`}
                data-testid={`cc-ai-counter-${c.label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/(^-|-$)/g, '')}`}>
                <span className={`w-9 h-9 rounded-sm flex items-center justify-center ${c.danger ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500'}`}><c.icon size={16} /></span>
                <div>
                  <div className={`font-display font-extrabold text-2xl tabular-nums ${c.danger ? 'text-rose-700' : 'text-[#0F172A]'}`}>{c.value}</div>
                  <div className="kpi-overline">{c.label}</div>
                </div>
              </a>
            ))}
          </div>
        );
      })()}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-4" data-testid="cc-ai-risk-signals">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="kpi-overline flex items-center gap-1.5"><Sparkles size={11} className="text-[#3b4872]" /> AI Risk Signals</div>
            <h3 className="font-display font-bold text-base mt-0.5">Projects flagged High/Critical by AI delay prediction</h3>
          </div>
          <span className="text-[10px] text-slate-400 normal-case">Stored predictions · generate in a project’s AI Insights tab</span>
        </div>
        <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
          {aiSignals.length === 0 ? (
            <div className="p-5 text-sm text-slate-400" data-testid="cc-ai-risk-empty">
              No AI delay predictions flagged High or Critical yet. Run a delay prediction from any project’s
              <span className="font-semibold"> AI Insights </span> tab to surface signals here.
            </div>
          ) : (
            <table className="w-full dense-table">
              <thead><tr><th>Project</th><th>Top risk</th><th className="text-right">Delay</th><th className="text-right">Risk</th></tr></thead>
              <tbody>
                {aiSignals.map((s, i) => (
                  <tr key={i} className="hover:bg-slate-50 cursor-pointer" data-testid={`cc-ai-signal-${i}`}
                    onClick={() => s.project_id && (window.location.href = `/app/projects/${s.project_id}`)}>
                    <td className="text-[13px] font-display font-semibold text-[#0F172A] max-w-[200px] truncate">{s.project_name || s.project_id}</td>
                    <td className="text-xs text-slate-500 max-w-[280px] truncate">{(s.top_risks || [])[0] || '—'}</td>
                    <td className="text-right text-xs font-display font-bold tabular-nums">{s.predicted_delay_days || '—'}{s.predicted_delay_days ? 'd' : ''}</td>
                    <td className="text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider ${s.risk_level === 'Critical' ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'}`}>
                        {s.risk_level}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Labour Today + Delay Signals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div className="bg-white border-l-4 border-l-blue-500 border border-slate-200 rounded-sm p-4" data-testid="cc-labour-today">
          <div className="kpi-overline mb-2 flex items-center gap-2"><Users size={11} /> Labour Today</div>
          <div className="font-display text-xl font-bold mb-0.5">{labour.present || 0} / {labour.total_active || 0} present</div>
          <div className="text-xs text-slate-600">{labour.attendance_pct || 0}% attendance{labour.anomaly_count ? ` · ${labour.anomaly_count} geofence anomalies` : ''}</div>
        </div>
        <div className="bg-white border-l-4 border-l-amber-500 border border-slate-200 rounded-sm p-4" data-testid="cc-delay-signals">
          <div className="kpi-overline mb-2 flex items-center gap-2"><Activity size={11} /> DPR Coverage</div>
          <div className="font-display text-xl font-bold mb-0.5">{delay.dpr_missed_today_projects || 0} missed today</div>
          <div className="text-xs text-slate-600">{delay.dpr_missed_3d_projects || 0} projects with no DPR in 3 days</div>
        </div>
        <div className="bg-white border-l-4 border-l-red-500 border border-slate-200 rounded-sm p-4" data-testid="cc-procurement-overdue">
          <div className="kpi-overline mb-2 flex items-center gap-2"><AlertOctagon size={11} /> Procurement Overdue</div>
          <div className="font-display text-xl font-bold mb-0.5">{delay.procurement_overdue_count || 0} deliveries</div>
          <div className="text-xs text-slate-600">Past expected delivery date across active POs</div>
        </div>
      </div>
    </div>
  );
}
